import React, { useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as SortIcon } from "src/images/up-down.svg";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { MetaFieldType } from "src/app/methods/getMetaFieldTypeOptions";
import DeleteMetaFieldModal from "src/app/modals/DeleteMetaFieldModal/DeleteMetaFieldModal";
import { TasksColumn } from "src/redux/task/taskReducer";
import { RootState } from "src/redux/reducers";
import { TableContext } from "src/app/project/TableField/types";
import {
  setWSTaskActiveSortingColumn,
  toggleWSTasksColumnVisibility,
} from "src/redux/workspaceTaskFilters/workspaceTaskFiltersActions";
import { openSettingsModal } from "src/app/SettingsModal/methods";
import { SettingsContext, SettingsTab } from "src/app/SettingsModal/enums";
import { ExtensionsSection } from "src/app/SettingsModal/tabs/Extensions/Extensions";
import { AppLocation } from "src/redux/main/mainReducer";
import {
  Dropdown,
  DropdownMenu,
  DropdownMenuItem,
} from "../../components/Dropdown/Dropdown";
import { ReactComponent as ClientGreyIcon } from "../../../images/client-grey.svg";
import { ReactComponent as ClientOrangeIcon } from "../../../images/client-orange.svg";
import { ReactComponent as EditIcon } from "../../../images/edit.svg";
import { ReactComponent as HideIcon } from "../../../images/hide.svg";
import { ReactComponent as MoveLeftIcon } from "../../../images/move-left.svg";
import { ReactComponent as MoveRightIcon } from "../../../images/move-right.svg";
import { ReactComponent as TickIcon } from "../../../images/tick.svg";
import { ReactComponent as DeleteIcon } from "../../../images/trash-can.svg";
import { generateRankString } from "../../../utils/rankStrings";
import {
  changeFieldVisibility,
  setActiveSortingColumn,
  updateTaskMetaFieldRank,
} from "../../../redux";
import { tableDataType } from "../../components/Table/Table";
import ManageFieldModal from "../../modals/ManageFieldModal/ManageFieldModal";

import "./TaskTableHeaderOptionsDropdown.scss";

interface TaskTableHeaderOptionsDropdownProps {
  uuid: string;
  columnFromApi?: TasksColumn;
  setIsDropdownOpened: React.Dispatch<React.SetStateAction<boolean>>;
  openShareWithClientModal?: () => void;
  openStopSharingWithClientModal?: () => void;
  tableContext: TableContext | undefined;
  activeSortingColumn: string | null;
}

function TaskTableHeaderOptionsDropdown({
  uuid,
  columnFromApi,
  setIsDropdownOpened,
  openShareWithClientModal,
  openStopSharingWithClientModal,
  tableContext,
  activeSortingColumn,
}: TaskTableHeaderOptionsDropdownProps) {
  const params = useParams<{ projectId: string }>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSetupModal, setShowSetupModal] = useState(false);

  const permissions = useSelector(
    (state: RootState) => state.projectReducer.projectBasicData?.permissions,
  );
  const clientMode = useSelector(
    (state: RootState) => state.projectReducer.projectBasicData?.clientMode,
  );

  const tasksColumns: TasksColumn[] = useSelector(
    (state: RootState) => state.taskReducer.tasksColumns,
  );

  const dispatch = useDispatch();
  const history = useHistory();

  const moveRight = () => {
    if (!columnFromApi) return;

    const colIndex = tasksColumns.findIndex(
      (x) => x.metaFieldId === columnFromApi.metaFieldId,
    );
    const columnsLength = tasksColumns.length;

    if (colIndex === columnsLength - 1) return;

    const newRank = generateRankString(
      tasksColumns[colIndex + 1].metaFieldRank,
      tasksColumns[colIndex + 2]?.metaFieldRank || "",
    );

    dispatch(
      updateTaskMetaFieldRank({
        metaFieldId: columnFromApi.metaFieldId,
        metaFieldRank: newRank,
      }),
    );
  };

  const moveLeft = () => {
    if (!columnFromApi) return;

    const colIndex = tasksColumns.findIndex(
      (x) => x.metaFieldId === columnFromApi.metaFieldId,
    );

    if (colIndex === 0) return;

    const newRank = generateRankString(
      tasksColumns[colIndex - 2]?.metaFieldRank || "",
      tasksColumns[colIndex - 1].metaFieldRank,
    );

    dispatch(
      updateTaskMetaFieldRank({
        metaFieldId: columnFromApi.metaFieldId,
        metaFieldRank: newRank,
      }),
    );
  };

  const changeVisibility = (newVisibilityValue: boolean) => {
    if (tableContext === "workspaceTasksList") {
      dispatch(toggleWSTasksColumnVisibility(uuid));
    } else if (columnFromApi) {
      dispatch(
        changeFieldVisibility(columnFromApi.metaFieldId, newVisibilityValue),
      );
    }
  };

  const handleClickEdit = () => {
    if (columnFromApi?.data?.wsCreatorContentMonitorUuid) {
      openSettingsModal(
        history,
        AppLocation.SingleProject,
        SettingsContext.Project,
        SettingsTab.Extensions,
        ExtensionsSection.CreatorContentMonitoring,
        columnFromApi.data.wsCreatorContentMonitorUuid,
      );
    } else {
      setShowSetupModal(true);
    }
  };

  return (
    <div>
      <Dropdown toRight className="tasks-table-header-options-dropdown">
        {(isOpen, setIsOpen) => {
          setIsDropdownOpened(isOpen);
          return (
            <>
              <div
                className="tasks-table-header-options-dropdown__dots"
                onClick={() => setIsOpen((prev) => !prev)}
              />
              <DropdownMenu
                isOpen={isOpen}
                onClick={() => setIsOpen((prev) => !prev)}
              >
                <DropdownMenuItem
                  onClick={() => {
                    if (activeSortingColumn !== uuid && uuid) {
                      if (tableContext === "workspaceTasksList") {
                        dispatch(setWSTaskActiveSortingColumn(uuid));
                      } else {
                        dispatch(setActiveSortingColumn(uuid));
                      }
                    } else {
                      if (tableContext === "workspaceTasksList") {
                        dispatch(setWSTaskActiveSortingColumn(""));
                      } else {
                        dispatch(setActiveSortingColumn(""));
                      }
                    }
                  }}
                >
                  <SortIcon />
                  <IDHFormattedMessage
                    id="ws_sort_by"
                    defaultMessage="Sort by"
                  />
                  {activeSortingColumn === uuid && (
                    <TickIcon className="dropdown__menu-item-tick" />
                  )}
                </DropdownMenuItem>
                {columnFromApi && (
                  <>
                    {permissions?.project.includes(
                      "task_meta_fields_management",
                    ) && (
                      <>
                        <DropdownMenuItem onClick={handleClickEdit}>
                          <EditIcon />
                          <IDHFormattedMessage
                            id="ws_edit"
                            defaultMessage="Edit"
                          />
                        </DropdownMenuItem>

                        <DropdownMenuItem onClick={moveLeft}>
                          <MoveLeftIcon />
                          <IDHFormattedMessage
                            id="ws_move_left"
                            defaultMessage="Move left"
                          />
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={moveRight}>
                          <MoveRightIcon />
                          <IDHFormattedMessage
                            id="ws_move_right"
                            defaultMessage="Move right"
                          />
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => changeVisibility(false)}
                        >
                          <HideIcon />
                          <IDHFormattedMessage
                            id="ws_hide"
                            defaultMessage="Hide"
                          />
                        </DropdownMenuItem>
                      </>
                    )}

                    {permissions?.project.includes("share_project_resources") &&
                      clientMode &&
                      columnFromApi?.metaFieldType !==
                        MetaFieldType.DictionaryElement && (
                        <>
                          {columnFromApi.metaFieldShared ? (
                            <DropdownMenuItem
                              onClick={openStopSharingWithClientModal}
                            >
                              <ClientGreyIcon />
                              <IDHFormattedMessage
                                id="ws_stop_sharing"
                                defaultMessage="Stop sharing"
                              />
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              onClick={openShareWithClientModal}
                            >
                              <ClientOrangeIcon />
                              <IDHFormattedMessage
                                id="ws_share_with_client"
                                defaultMessage="Share with Client"
                              />
                            </DropdownMenuItem>
                          )}
                        </>
                      )}

                    {!columnFromApi?.metaFieldIsRequired &&
                      permissions?.project.includes(
                        "task_meta_fields_management",
                      ) && (
                        <DropdownMenuItem
                          onClick={() => setShowDeleteModal(true)}
                          className="dropdown__menu-item--danger"
                        >
                          <DeleteIcon />
                          <IDHFormattedMessage
                            id="ws_remove"
                            defaultMessage="Remove"
                          />
                        </DropdownMenuItem>
                      )}
                  </>
                )}

                {tableContext === "workspaceTasksList" && (
                  <DropdownMenuItem onClick={() => changeVisibility(false)}>
                    <HideIcon />
                    <IDHFormattedMessage id="ws_hide" defaultMessage="Hide" />
                  </DropdownMenuItem>
                )}
              </DropdownMenu>
            </>
          );
        }}
      </Dropdown>

      {showDeleteModal && columnFromApi && (
        <DeleteMetaFieldModal
          uuid={uuid}
          fieldName={columnFromApi?.metaFieldName}
          onClose={() => setShowDeleteModal(false)}
          dataType={tableDataType.Task}
        />
      )}

      {showSetupModal && columnFromApi && (
        <ManageFieldModal
          // remap object props to format acceptable by ManageFieldModal
          data={{
            id: columnFromApi.metaFieldId,
            isRequired: columnFromApi.metaFieldIsRequired,
            isVisible: columnFromApi.metaFieldVisible,
            key: columnFromApi.metaFieldKey,
            metaFieldOptions: columnFromApi.data,
            metaFieldSource: columnFromApi.metaFieldValueSource,
            name: columnFromApi.metaFieldName,
            rank: columnFromApi.metaFieldRank,
            title: columnFromApi.metaFieldName,
            type: columnFromApi.metaFieldType,
            valueSource: columnFromApi.metaFieldValueSource,
            wsGlobalTaskMetaFieldUuid:
              columnFromApi.metaFieldGlobalTaskMetaFieldUuid,
            canEdit: columnFromApi.metaFieldCanEdit !== false,
          }}
          onClose={() => setShowSetupModal(false)}
          dataType={tableDataType.Task}
          projectId={params.projectId}
        />
      )}
    </div>
  );
}

export { TaskTableHeaderOptionsDropdown };
